/**
 * TimelineRenderer — the timeline / swimlane lens.
 *
 * Lays out parallel lanes (swimlanes) on Y and time on X. Each event is a
 * rounded bar spanning [start, end] within its lane; zero-duration events
 * render as a marker dot. Lanes get alternating faint background stripes and a
 * left-margin label column; the bottom carries a few axis ticks.
 *
 * Like ForceRenderer, the look is driven entirely by a Theme: the dark
 * "constellation" variant paints luminous bars on a near-black field; the
 * editorial variant paints ink bars with hairline borders on warm white. Bars
 * are colored categorically by lane via theme.palette (top lanes colored, rest
 * neutral). A node.status-style tier adds an optional glow/ring.
 *
 * Time values may be day offsets or ms timestamps — the renderer treats them as
 * plain numbers and scales linearly, so the example build() functions document
 * the unit they chose.
 *
 * HEADLESS-SAFE: all layout math is done arithmetically (d3-scale only). No
 * getBBox / getComputedTextLength / getBoundingClientRect.
 */

import { scaleLinear, scaleBand } from "d3-scale";
import { select, type Selection } from "d3-selection";

import { THEMES, type Theme } from "../theme.js";
import type {
  LensExample,
  LensRenderer,
  LensRendererOptions,
} from "../lens.js";

type EventStatus = "active" | "gap" | "hub" | "trending";

interface TimelineLane {
  id: string;
  label: string;
}

interface TimelineEvent {
  lane: string;
  label: string;
  start: number;
  end?: number;
  status?: EventStatus;
}

interface TimelineData {
  title: string;
  start: number;
  end: number;
  lanes: TimelineLane[];
  events: TimelineEvent[];
}

const SVG_NS = "http://www.w3.org/2000/svg";

export class TimelineRenderer implements LensRenderer {
  private readonly svg: Selection<SVGSVGElement, unknown, null, undefined>;
  private readonly width: number;
  private readonly height: number;
  private readonly theme: Theme;
  private readonly showLabels: boolean;
  private data: TimelineData | null = null;

  constructor(
    container: SVGSVGElement | HTMLElement,
    opts: LensRendererOptions = {},
  ) {
    this.width = opts.width ?? 960;
    this.height = opts.height ?? 720;
    this.theme =
      typeof opts.theme === "object"
        ? opts.theme
        : THEMES[opts.theme ?? "dark"];
    this.showLabels = opts.showLabels ?? true;

    if (container instanceof SVGSVGElement) {
      this.svg = select(container);
    } else {
      const svgEl = document.createElementNS(SVG_NS, "svg");
      container.appendChild(svgEl);
      this.svg = select(svgEl as SVGSVGElement);
    }

    this.svg
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", this.theme.background);
  }

  render(data: unknown): this {
    this.data = data as TimelineData;
    this.draw();
    return this;
  }

  /** Static layout — nothing to settle. */
  settle(_ticks?: number): this {
    return this;
  }

  exportSVG(): string {
    const node = this.svg.node();
    if (!node) return "";
    const clone = node.cloneNode(true) as SVGSVGElement;
    return new XMLSerializer().serializeToString(clone);
  }

  destroy(): void {
    this.svg.selectAll("*").remove();
  }

  // ── internals ─────────────────────────────────────────────────────────

  private draw(): void {
    this.svg.selectAll("*").remove();
    const theme = this.theme;
    const data = this.data;
    if (!data) return;

    // Background rect so the exported SVG carries its field.
    this.svg
      .append("rect")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("fill", theme.background);

    const fs = theme.label.fontSize;
    const titleFs = fs + 6;

    // Left margin reserves room for lane labels; estimate widest label.
    const widestLane = Math.max(
      0,
      ...data.lanes.map((l) => estTextWidth(l.label, fs)),
    );
    const padLeft = Math.min(180, Math.max(140, widestLane + 28));
    const padRight = 48;
    const padTop = 48 + titleFs + 12;
    const padBottom = 48 + fs + 18; // axis ticks

    const plotW = Math.max(1, this.width - padLeft - padRight);
    const plotH = Math.max(1, this.height - padTop - padBottom);

    const x = scaleLinear()
      .domain([data.start, data.end])
      .range([padLeft, padLeft + plotW]);

    const y = scaleBand<string>()
      .domain(data.lanes.map((l) => l.id))
      .range([padTop, padTop + plotH])
      .paddingInner(0.28)
      .paddingOuter(0.12);

    const bandH = y.bandwidth();

    // Categorical lane colors: rank lanes by event count, color top N.
    const colorOf = this.buildLaneColors(data);

    const defs = this.svg.append("defs");
    this.buildDefs(defs, data, colorOf);

    const content = this.svg.append("g").attr("class", "content");

    // ── alternating lane background stripes ──
    const lanesG = content.append("g").attr("class", "lanes");
    data.lanes.forEach((lane, i) => {
      const ly = y(lane.id);
      if (ly === undefined) return;
      // Stripe spans the full plot row (including the band padding gap).
      const step = y.step();
      const stripeY = padTop + i * step;
      lanesG
        .append("rect")
        .attr("x", padLeft)
        .attr("y", stripeY)
        .attr("width", plotW)
        .attr("height", step)
        .attr("fill", stripeFill(theme, i))
        .attr("opacity", i % 2 === 0 ? 1 : 0.45);
    });

    // ── lane labels (left column) ──
    if (this.showLabels) {
      const laneLabelsG = content.append("g").attr("class", "lane-labels");
      data.lanes.forEach((lane) => {
        const ly = y(lane.id);
        if (ly === undefined) return;
        laneLabelsG
          .append("text")
          .attr("x", padLeft - 12)
          .attr("y", ly + bandH / 2)
          .attr("text-anchor", "end")
          .attr("dominant-baseline", "central")
          .attr("font-family", theme.label.fontFamily)
          .attr("font-size", fs + 1)
          .attr("font-weight", 600)
          .attr("fill", theme.label.color)
          .attr("paint-order", "stroke")
          .attr("stroke", theme.background)
          .attr("stroke-width", 2.5)
          .attr("stroke-linejoin", "round")
          .text(lane.label);
      });
    }

    // ── axis (bottom) ──
    this.drawAxis(content, x, data, padTop + plotH, padBottom);

    // ── events ──
    const eventsG = content.append("g").attr("class", "events");
    const barH = Math.max(8, Math.min(bandH, 28));
    const isDark = theme.name === "dark";

    for (const ev of data.events) {
      const ly = y(ev.lane);
      if (ly === undefined) continue; // event references unknown lane
      const color = colorOf(ev.lane);
      const cy = ly + bandH / 2;
      const x0 = clampX(x(ev.start), padLeft, padLeft + plotW);
      const tier = glowTier(ev.status);

      const g = eventsG.append("g").attr("class", "event");

      const isPoint = ev.end === undefined || ev.end <= ev.start;

      if (isPoint) {
        // Zero-duration milestone → marker dot (diamond reads as a milestone).
        const r = Math.max(5, barH / 2.2);
        if (tier > 0) {
          g.append("circle")
            .attr("cx", x0)
            .attr("cy", cy)
            .attr("r", r * theme.glow.outerScale + 2)
            .attr(
              "fill",
              theme.glow.outerColor === "white"
                ? `rgba(255,255,255,${theme.glow.haloOpacity})`
                : withAlpha(color, theme.glow.haloOpacity),
            )
            .attr("filter", `url(#tl-glow-${tier})`);
        }
        const d = diamondPath(x0, cy, r);
        const mark = g
          .append("path")
          .attr("d", d)
          .attr("fill", color)
          .attr("fill-opacity", isDark ? 0.95 : 0.98);
        if (tier > 0 && theme.glow.ring) {
          mark
            .attr("stroke", theme.glow.ring.color)
            .attr("stroke-width", theme.glow.ring.widthByTier[tier]);
        } else if (theme.node.stroke) {
          mark
            .attr("stroke", theme.node.stroke)
            .attr("stroke-width", theme.node.strokeWidth ?? 1);
        }
        // Label sits centered above the marker, so it never collides with a
        // bar that starts immediately to the right of the milestone.
        if (this.showLabels) {
          this.drawEventLabel(g, ev.label, x0, cy - r - 5, "middle", color);
        }
      } else {
        const x1 = clampX(x(ev.end as number), padLeft, padLeft + plotW);
        const bw = Math.max(3, x1 - x0);
        const by = cy - barH / 2;
        const rx = Math.min(barH / 2, 7);

        if (tier > 0) {
          g.append("rect")
            .attr("x", x0 - 2)
            .attr("y", by - 2)
            .attr("width", bw + 4)
            .attr("height", barH + 4)
            .attr("rx", rx + 2)
            .attr(
              "fill",
              theme.glow.outerColor === "white"
                ? `rgba(255,255,255,${theme.glow.haloOpacity})`
                : withAlpha(color, theme.glow.haloOpacity),
            )
            .attr("filter", `url(#tl-glow-${tier})`);
        }

        const bar = g
          .append("rect")
          .attr("x", x0)
          .attr("y", by)
          .attr("width", bw)
          .attr("height", barH)
          .attr("rx", rx)
          .attr("ry", rx)
          .attr("fill", color)
          .attr("fill-opacity", isDark ? 0.85 : 0.92);

        if (ev.status === "gap") {
          // Gaps read as hollow/dashed — work that isn't happening yet.
          bar
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", "4 3");
        } else if (tier > 0 && theme.glow.ring) {
          bar
            .attr("stroke", theme.glow.ring.color)
            .attr("stroke-width", theme.glow.ring.widthByTier[tier]);
        } else if (theme.node.stroke) {
          bar
            .attr("stroke", theme.node.stroke)
            .attr("stroke-width", theme.node.strokeWidth ?? 1);
        }

        if (this.showLabels) {
          // Place the label inside the bar if it fits, else just to the right.
          const labelW = estTextWidth(ev.label, fs);
          const fitsInside = labelW + 12 < bw;
          if (fitsInside && ev.status !== "gap") {
            this.drawEventLabel(
              g,
              ev.label,
              x0 + 8,
              cy,
              "start",
              color,
              insideTextColor(theme),
            );
          } else {
            const rightEdge = x0 + bw + 6;
            // If the bar ends near the right margin, anchor the label to the
            // left of the bar end instead so it stays inside the plot.
            if (rightEdge + labelW > padLeft + plotW) {
              this.drawEventLabel(g, ev.label, x0 + bw - 6, cy, "end", color);
            } else {
              this.drawEventLabel(g, ev.label, rightEdge, cy, "start", color);
            }
          }
        }
      }
    }

    // ── title ──
    if (this.showLabels) {
      content
        .append("text")
        .attr("x", padLeft)
        .attr("y", 48)
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", titleFs)
        .attr("font-weight", 700)
        .attr("fill", theme.label.color)
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 3)
        .attr("stroke-linejoin", "round")
        .text(data.title);
    }
  }

  private drawEventLabel(
    g: Selection<SVGGElement, unknown, null, undefined>,
    text: string,
    tx: number,
    cy: number,
    anchor: "start" | "end" | "middle",
    haloColor: string,
    fillColor?: string,
  ): void {
    const theme = this.theme;
    g.append("text")
      .attr("x", tx)
      .attr("y", cy)
      .attr("text-anchor", anchor)
      .attr("dominant-baseline", "central")
      .attr("font-family", theme.label.fontFamily)
      .attr("font-size", theme.label.fontSize)
      .attr("fill", fillColor ?? theme.label.color)
      .attr("paint-order", "stroke")
      .attr("stroke", fillColor ? haloColor : theme.background)
      .attr("stroke-width", 2.5)
      .attr("stroke-linejoin", "round")
      .text(text);
  }

  private drawAxis(
    content: Selection<SVGGElement, unknown, null, undefined>,
    x: (n: number) => number,
    data: TimelineData,
    axisY: number,
    _padBottom: number,
  ): void {
    const theme = this.theme;
    const fs = theme.label.fontSize;
    const axisG = content.append("g").attr("class", "axis");

    // Baseline.
    axisG
      .append("line")
      .attr("x1", x(data.start))
      .attr("y1", axisY)
      .attr("x2", x(data.end))
      .attr("y2", axisY)
      .attr("stroke", theme.label.valueColor)
      .attr("stroke-width", 1);

    const ticks = niceTicks(data.start, data.end, 6);
    for (const t of ticks) {
      const tx = x(t);
      axisG
        .append("line")
        .attr("x1", tx)
        .attr("y1", axisY)
        .attr("x2", tx)
        .attr("y2", axisY + 5)
        .attr("stroke", theme.label.valueColor)
        .attr("stroke-width", 1);
      axisG
        .append("text")
        .attr("x", tx)
        .attr("y", axisY + 6 + fs)
        .attr("text-anchor", "middle")
        .attr("font-family", theme.label.valueFontFamily ?? theme.label.fontFamily)
        .attr("font-size", fs)
        .attr("fill", theme.label.valueColor)
        .text(formatTick(t, data.start, data.end));
    }
  }

  private buildLaneColors(data: TimelineData): (laneId: string) => string {
    // Rank lanes by event count; color the top N, rest neutral. Lanes keep
    // their declared order for stable coloring within the top set.
    const count = new Map<string, number>();
    for (const lane of data.lanes) count.set(lane.id, 0);
    for (const ev of data.events) {
      if (count.has(ev.lane)) count.set(ev.lane, (count.get(ev.lane) ?? 0) + 1);
    }
    const ranked = data.lanes
      .slice()
      .sort((a, b) => (count.get(b.id) ?? 0) - (count.get(a.id) ?? 0))
      .slice(0, this.theme.maxColoredClusters)
      .map((l) => l.id);
    const colorByLane = new Map<string, string>();
    ranked.forEach((id, i) => colorByLane.set(id, this.theme.palette[i]));
    return (laneId) => colorByLane.get(laneId) ?? this.theme.neutralCluster;
  }

  private buildDefs(
    defs: Selection<SVGDefsElement, unknown, null, undefined>,
    data: TimelineData,
    colorOf: (laneId: string) => string,
  ): void {
    const tiers = new Set<number>();
    for (const ev of data.events) {
      const t = glowTier(ev.status);
      if (t > 0) tiers.add(t);
    }
    for (const tier of tiers) {
      const filter = defs
        .append("filter")
        .attr("id", `tl-glow-${tier}`)
        .attr("x", "-75%")
        .attr("y", "-75%")
        .attr("width", "250%")
        .attr("height", "250%");
      filter
        .append("feGaussianBlur")
        .attr("in", "SourceGraphic")
        .attr("stdDeviation", this.theme.glow.blurByTier[tier as 1 | 2 | 3]);
    }
    void colorOf;
  }
}

// ── pure helpers ──────────────────────────────────────────────────────────

function estTextWidth(text: string, fontSize: number): number {
  return text.length * fontSize * 0.6;
}

function glowTier(status?: EventStatus): 0 | 1 | 2 | 3 {
  switch (status) {
    case "trending":
      return 3;
    case "hub":
      return 2;
    case "active":
      return 1;
    default:
      // "gap" gets its own hollow treatment, not a glow.
      return 0;
  }
}

function clampX(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function diamondPath(cx: number, cy: number, r: number): string {
  return `M ${cx} ${cy - r} L ${cx + r} ${cy} L ${cx} ${cy + r} L ${cx - r} ${cy} Z`;
}

function stripeFill(theme: Theme, i: number): string {
  // Subtle alternating stripe derived from the theme so it stays on-brand.
  if (theme.name === "dark") {
    return i % 2 === 0 ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.015)";
  }
  return i % 2 === 0 ? "rgba(26,26,26,0.05)" : "rgba(26,26,26,0.02)";
}

function insideTextColor(theme: Theme): string {
  // White inside dark-theme bars; warm-white inside editorial ink bars.
  return theme.name === "dark" ? "rgba(255,255,255,0.95)" : "#FAF8F5";
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** A handful of evenly spaced, rounded tick values across [start, end]. */
function niceTicks(start: number, end: number, count: number): number[] {
  const span = end - start;
  if (span <= 0) return [start];
  const rawStep = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let stepMul: number;
  if (norm < 1.5) stepMul = 1;
  else if (norm < 3) stepMul = 2;
  else if (norm < 7) stepMul = 5;
  else stepMul = 10;
  const step = stepMul * mag;
  const first = Math.ceil(start / step) * step;
  const ticks: number[] = [];
  for (let t = first; t <= end + 1e-9; t += step) {
    // Guard against float drift producing -0 / tiny noise.
    ticks.push(Math.abs(t) < 1e-9 ? 0 : t);
  }
  if (ticks.length === 0) ticks.push(start, end);
  return ticks;
}

function formatTick(t: number, start: number, end: number): string {
  const span = end - start;
  // Heuristic: large numbers are ms timestamps; small ones are offsets.
  if (start > 1e11) {
    const d = new Date(t);
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    if (span <= 2 * 24 * 3600 * 1000) {
      // Sub-2-day span → show hours.
      const h = d.getUTCHours();
      return `${String(h).padStart(2, "0")}:00`;
    }
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
  }
  // Plain offset units.
  return Number.isInteger(t) ? String(t) : t.toFixed(1);
}

// ── examples ────────────────────────────────────────────────────────────────

const DAY = 24 * 3600 * 1000;

export const TIMELINE_EXAMPLES: LensExample[] = [
  {
    id: "timeline-totem-roadmap",
    title: "Totem Roadmap Q1–Q3 2026",
    blurb:
      "Quarterly milestones across the Totem, Jargon, ShurAI, and Content streams, in day-offsets from Jan 1.",
    highlights: ["4 lanes", "milestones + spans", "status glows"],
    lens: "timeline",
    // Time unit: day offsets from Jan 1 2026 (0 = Jan 1, ~273 = end of Sep).
    build: (): TimelineData => ({
      title: "Totem Roadmap — Q1 to Q3 2026",
      start: 0,
      end: 273,
      lanes: [
        { id: "totem", label: "Totem Protocol" },
        { id: "jargon", label: "Jargon" },
        { id: "shurai", label: "ShurAI" },
        { id: "content", label: "Content" },
      ],
      events: [
        { lane: "totem", label: "Letta memory integration", start: 6, end: 70, status: "active" },
        { lane: "totem", label: "GenAI Design Studio", start: 78, end: 168, status: "hub" },
        { lane: "totem", label: "MMA curriculum", start: 176, end: 268, status: "trending" },
        { lane: "totem", label: "v1 launch", start: 70 },
        { lane: "jargon", label: "Modular prompt fork", start: 10, end: 88 },
        { lane: "jargon", label: "Workspace scoping", start: 92, end: 150 },
        { lane: "jargon", label: "Marketplace plugin", start: 155, end: 230, status: "gap" },
        { lane: "shurai", label: "Client pilot", start: 20, end: 95 },
        { lane: "shurai", label: "Content-as-a-service GA", start: 100, end: 200, status: "active" },
        { lane: "shurai", label: "Condo Experts pitch", start: 24 },
        { lane: "content", label: "Flywheel mapping", start: 0, end: 40 },
        { lane: "content", label: "Vault → publish pipeline", start: 44, end: 130 },
        { lane: "content", label: "Social cadence", start: 134, end: 260, status: "active" },
      ],
    }),
  },
  {
    id: "timeline-bot-activity-24h",
    title: "Bot Activity (24h)",
    blurb:
      "Short processing bursts across the nine-bot Telegram ecosystem over a single day, as ms timestamps.",
    highlights: ["9 lanes", "burst events", "ms timeline"],
    lens: "timeline",
    // Time unit: ms timestamps across one UTC day.
    build: (): TimelineData => {
      const t0 = Date.UTC(2026, 5, 1, 0, 0, 0); // Jun 1 2026 00:00 UTC
      const h = (hours: number) => t0 + hours * 3600 * 1000;
      return {
        title: "Bot Activity — 24 Hours",
        start: t0,
        end: t0 + DAY,
        lanes: [
          { id: "logger", label: "Message Logger" },
          { id: "link", label: "Link Processor" },
          { id: "classifier", label: "Classifier" },
          { id: "bridge", label: "Context Bridge" },
          { id: "action", label: "Action Extractor" },
          { id: "insight", label: "Insight Synthesizer" },
          { id: "roam", label: "Roam Export" },
          { id: "kg", label: "Knowledge Graph" },
          { id: "factory", label: "Content Factory" },
        ],
        events: [
          { lane: "logger", label: "ingest", start: h(0.5), end: h(2) },
          { lane: "logger", label: "ingest", start: h(9), end: h(11), status: "active" },
          { lane: "logger", label: "ingest", start: h(20), end: h(22.5) },
          { lane: "link", label: "fetch+unfurl", start: h(2), end: h(2.6) },
          { lane: "link", label: "fetch+unfurl", start: h(11.2), end: h(11.9) },
          { lane: "classifier", label: "tag", start: h(2.7), end: h(3.4) },
          { lane: "classifier", label: "tag", start: h(12), end: h(12.7), status: "hub" },
          { lane: "bridge", label: "sync", start: h(3.5), end: h(4) },
          { lane: "action", label: "extract", start: h(4.1), end: h(4.7) },
          { lane: "action", label: "extract", start: h(13), end: h(13.6) },
          { lane: "insight", label: "synthesize", start: h(5), end: h(6.5), status: "trending" },
          { lane: "roam", label: "export", start: h(6.6) },
          { lane: "kg", label: "rebuild graph", start: h(7), end: h(8.5), status: "active" },
          { lane: "kg", label: "rebuild graph", start: h(18), end: h(19.5) },
          { lane: "factory", label: "draft", start: h(8.6), end: h(10) },
          { lane: "factory", label: "publish", start: h(15) },
          { lane: "factory", label: "draft", start: h(21), end: h(23) },
        ],
      };
    },
  },
  {
    id: "timeline-session-history",
    title: "Session History",
    blurb:
      "A week of Claude Code work sessions across projects, in hour-offsets from Monday 00:00.",
    highlights: ["6 lanes", "varied durations", "weekly view"],
    lens: "timeline",
    // Time unit: hour offsets from Monday 00:00 (0 = Mon, 168 = next Mon).
    build: (): TimelineData => ({
      title: "Work Sessions — One Week (hours from Mon)",
      start: 0,
      end: 168,
      lanes: [
        { id: "totem", label: "Totem" },
        { id: "vizstudio", label: "Viz Studio" },
        { id: "telegram", label: "SC Telegram Bots" },
        { id: "lettabridge", label: "lettabridge" },
        { id: "vault", label: "Obsidian Vault" },
        { id: "pitch", label: "Condo Pitch" },
      ],
      events: [
        { lane: "totem", label: "registry refactor", start: 9, end: 13, status: "active" },
        { lane: "totem", label: "persona index", start: 33, end: 36 },
        { lane: "totem", label: "Letta wiring", start: 105, end: 112, status: "hub" },
        { lane: "vizstudio", label: "force lens", start: 10, end: 16 },
        { lane: "vizstudio", label: "timeline lens", start: 56, end: 64, status: "trending" },
        { lane: "vizstudio", label: "gallery polish", start: 132, end: 137 },
        { lane: "telegram", label: "classifier fix", start: 14, end: 17 },
        { lane: "telegram", label: "insight bot", start: 80, end: 86 },
        { lane: "lettabridge", label: "memory bridge", start: 58, end: 63 },
        { lane: "lettabridge", label: "checkpoint", start: 108 },
        { lane: "vault", label: "flywheel map", start: 36, end: 41, status: "active" },
        { lane: "vault", label: "daily notes", start: 60 },
        { lane: "vault", label: "base dashboards", start: 130, end: 134 },
        { lane: "pitch", label: "draft deck", start: 84, end: 90 },
        { lane: "pitch", label: "rehearse", start: 150, end: 152 },
      ],
    }),
  },
];
